import { useEffect, useState } from 'react';

import config from '@/config';
import { isReachable } from '@shared/lib/connectivity';

// `isReachable` lives in `@shared/lib/connectivity` -- a plain, stateless
// function, so anything that needs the answer (this hook, `sw.version.ts`)
// calls it rather than reading a shared variable. Re-exported here because
// callers already import it from `@shared/lib/hooks`.
export { isReachable } from '@shared/lib/connectivity';

/**
 * React hook that returns if the application is online
 * is click outside the reference element
 *
 * @param host - Is the host to see if it's reachable
 * @return The service worker
 *
 */
export function useIsOnline(h?: string): boolean {
	const host: string = h ?? config.build.host;
	const [onlineStatus, setOnlineStatus] = useState<boolean>(navigator.onLine);
	useEffect(() => {
		// `isReachable`'s own `timeoutMs` (5s default) means this can still
		// be in flight well after unmount -- a template-smoke-style test
		// that mounts and tears down many components quickly hits this
		// routinely. React 18 silently no-ops a state update on an
		// unmounted component; React 19's scheduler doesn't guarantee that
		// once the environment itself (not just the component) is gone --
		// observed as a hard `ReferenceError: window is not defined` thrown
		// from inside React's own update-priority resolution, not from
		// this hook's code, when Vitest tears down jsdom's `window`
		// between test files while this promise is still pending. Guard
		// explicitly instead of relying on React's unmounted-update
		// handling.
		let cancelled = false;

		const checkReachability = () => {
			// `isReachable` *resolves* a boolean -- it catches internally and
			// never rejects. This used to be
			// `.then(() => setOnlineStatus(true)).catch(...)`, which threw the
			// answer away: the `.then` fired for `false` too and the `.catch`
			// was unreachable, so the hook reported "online" permanently, on
			// every template. Everything downstream of it -- the offline
			// notice, `setCacheDurationsForConnectivity`'s 24h offline
			// window, `getQueryCacheOptions`'s offline branch -- was
			// therefore dead code in practice.
			isReachable(host).then((reachable) => {
				if (!cancelled) setOnlineStatus(reachable);
			});
		};

		// The browser's `online` event only means a network interface came
		// up -- a captive portal, a VPN that's connected but not routing, or
		// DNS trouble all fire it while the actual backend stays
		// unreachable. Re-verify instead of trusting it directly, same
		// reasoning as sw.version.ts's update check.
		checkReachability();

		const offlineHandler = () => {
			// The browser's `offline` event is a reliable *true negative* (an
			// interface really did go down), unlike `online`, which only means
			// one came up -- hence no re-verification here.
			setOnlineStatus(false);
		};

		window.addEventListener('online', checkReachability);
		window.addEventListener('offline', offlineHandler);

		return () => {
			cancelled = true;
			window.removeEventListener('online', checkReachability);
			window.removeEventListener('offline', offlineHandler);
		};
	}, [host]);
	return onlineStatus;
}
